import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { useLocalStore, useObserver } from 'mobx-react';
import Avatar from '@/components/PC/Avatar';
import Style from './index.less';
import { RecordMessage } from '@/types/record';
import clientModels from '@/client/models';
import { durationTime } from '@/utils/common';

const $c = classnames.bind(Style);

function insertBr(text = ''): string {
  return text.replace(/\n/g, '<br/>');
}

interface RecordItemPros {
  msg: RecordMessage;
  preMsg: RecordMessage | undefined;
  className?: string;
}

const userCacheMap = {};
const userMap = {};

function getUserByYxuid(yxUid: string, channelId: number) {
  if (userMap[yxUid]) return userMap[yxUid];
  userMap[yxUid] = new Promise((resolve, reject) => {
    API.client.memberV3.queryMembers
      .request(
        {},
        {
          channelId,
          yxUids: [yxUid],
        }
      )
      .then((res) => {
        if (res.data?.[0]) {
          userCacheMap[yxUid] = res.data?.[0];
          resolve(res.data[0]);
        } else {
          delete userMap[yxUid];
        }
      })
      .catch((err) => {
        delete userMap[yxUid];
      });
  });
  return userMap[yxUid];
}

const RecordItem: React.FC<RecordItemPros> = function (props: RecordItemPros) {
  const { msg, preMsg, ...otherProps } = props;
  const { info } = useLocalStore(() => clientModels);
  const [userInfo, setUserInfo] = useState<null | defs.client.ParticipantsInformationV3>(
    userCacheMap[msg?.yxuid] || null
  );

  useEffect(() => {
    if (userInfo?.yxUid !== msg.yxuid) {
      getUserByYxuid(msg.yxuid, info.meetingInfo?.channelId).then((user) => {
        setUserInfo(user);
      });
    }
  }, [info.meetingInfo?.channelId, userInfo?.yxUid, msg.yxuid]);

  const startTime = useMemo(
    () => new Date(info.meetingInfo?.beginTime || 0).getTime(),
    [info.meetingInfo?.beginTime]
  );

  // 会议持续时间展示
  const time = useMemo(() => {
    if (!startTime) return '00:00';
    return durationTime(msg.timestamp, startTime);
  }, [startTime, msg.timestamp]);

  const needHeader = useMemo(() => {
    const minute = 1000 * 60;
    return !(
      msg.yxuid === preMsg?.yxuid &&
      Math.floor((msg.timestamp - startTime) / minute) ===
        Math.floor((preMsg?.timestamp - startTime) / minute)
    );
  }, [msg.timestamp, preMsg?.timestamp, startTime]);
  return useObserver(() => {
    return (
      <div
        {...otherProps}
        key={msg.id}
        // className={$c('msg-item', { 'msg-my': msg.flow === 'out' })}
        data-msgid={msg.id}
        className={classnames(
          $c('msg-item', { 'no-header': !needHeader, 'is-first': !preMsg }),
          otherProps.className || ''
        )}
      >
        <div className={$c('msg-left')}>
          {needHeader && (
            <Avatar
              size={isMobile ? 32 : 28}
              type="cover"
              url={userInfo?.memberAvatarUrl || msg.url}
              name={msg.name}
            />
          )}
        </div>
        <div className={$c('msg-right')}>
          {needHeader && (
            <div className={$c('msg-header')}>
              <span className={$c('nick-name')}>
                {userInfo?.memberName || msg.name || '&nbsp;'}
              </span>
              <span className={$c('msg-time')}>{time}</span>
            </div>
          )}
          <div className={$c('msg-content', { 'is-temp': msg.sentenceType === 2 })}>
            <span dangerouslySetInnerHTML={{ __html: insertBr(msg.subtitleContent) }} />
          </div>
        </div>
      </div>
    );
  });
};

export default RecordItem;
